/**
 * Temperature and moisture.
 *
 * Temperature is the easy half: latitude sets it, altitude cools it, a little noise
 * keeps the bands from looking like stripes.
 *
 * Moisture is the half that decides whether a world looks designed. Scattering
 * deserts by noise gives you sand next to swamp for no reason. Instead, moisture is
 * *advected*: prevailing winds pick up water crossing the sea, carry it inland, and
 * drop it as they climb. Air that has crossed a mountain range arrives dry, so the
 * far side is desert — the Gobi, the Atacama, the Great Basin. It costs one pass per
 * row and produces geography a player can reason about: mountains here mean drylands
 * there, and rivers on the windward side.
 */

import type { RngState } from '../rng/index.js';
import { latitudeOf } from './elevation.js';
import { createNoise, cylindricalFbm } from './noise.js';
import type { MapgenParams } from './params.js';

export interface ClimateField {
  /** 0 = polar, 1 = equatorial. */
  temperature: Float32Array;
  /** 0 = arid, 1 = saturated. */
  moisture: Float32Array;
}

/**
 * Prevailing wind direction for a row: +1 blows east, -1 blows west.
 *
 * Earth's three-cell circulation gives easterly trade winds in the tropics,
 * westerlies in the mid-latitudes and easterlies again at the poles. Reproducing
 * that is nearly free and makes continents asymmetric in a way that feels right —
 * wet west coasts in the mid-latitudes, wet east coasts in the tropics.
 */
export function prevailingWind(latitude: number): 1 | -1 {
  if (latitude < 0.33) return -1; // trade winds
  if (latitude < 0.66) return 1; // westerlies
  return -1; // polar easterlies
}

export function buildClimate(
  width: number,
  height: number,
  wrapX: boolean,
  elevation: Float32Array,
  seaLevel: number,
  isLand: Uint8Array,
  params: MapgenParams,
  rng: RngState,
): ClimateField {
  const tiles = width * height;
  const temperature = new Float32Array(tiles);
  const moisture = new Float32Array(tiles);

  const temperatureNoise = createNoise(rng);

  // ── temperature ──────────────────────────────────────────────────────────────
  for (let row = 0; row < height; row++) {
    const latitude = latitudeOf(row, height);
    // Cosine rather than linear: the tropics are broad and the temperate band is
    // narrow, which is both true and better for gameplay — more usable land.
    const base = Math.cos((latitude * Math.PI) / 2) ** 0.7;

    for (let col = 0; col < width; col++) {
      const index = row * width + col;
      // Lapse rate: only height *above sea level* cools a tile.
      const altitude = Math.max(0, elevation[index]! - seaLevel) / Math.max(1e-6, 1 - seaLevel);
      const wobble = cylindricalFbm(temperatureNoise, col, row, width, 3, { octaves: 3 }) * params.temperatureNoise;
      temperature[index] = Math.min(1, Math.max(0, base - altitude * 0.45 + wobble));
    }
  }

  // ── moisture ─────────────────────────────────────────────────────────────────
  //
  // For each row we walk a parcel of air along the prevailing wind. Over water it
  // takes up moisture; over land it rains, losing more the harder it has to climb.
  // What we record is the parcel's remaining humidity as a fraction of what it could
  // hold — which is exactly "how wet is the air over this tile", naturally in [0, 1].
  //
  // On a wrapping map the parcel leaving the east edge is the parcel arriving at the
  // west edge, so we walk two full laps and only record on the second. Recording the
  // first lap would leave every row with an artificial dry streak at its start column.
  const wrapColumn = (col: number): number => ((col % width) + width) % width;

  for (let pass = 0; pass < params.moisturePasses; pass++) {
    for (let row = 0; row < height; row++) {
      const latitude = latitudeOf(row, height);
      const wind = prevailingWind(latitude);
      // Cold air holds far less water. This is why the polar regions are deserts by
      // rainfall even where they are buried in ice.
      const capacity = 0.25 + 0.75 * Math.cos((latitude * Math.PI) / 2);

      const laps = wrapX ? 2 : 1;
      const steps = width * laps;
      const start = wind === 1 ? 0 : width - 1;

      // Later passes resume from where the previous one settled, which lets a large
      // continent's interior dry out progressively rather than in one step.
      const startIndex = row * width + start;
      let humidity = pass === 0 ? capacity * 0.6 : moisture[startIndex]! * capacity;

      for (let step = 0; step < steps; step++) {
        const rawCol = start + wind * step;
        if (!wrapX && (rawCol < 0 || rawCol >= width)) break;
        const col = wrapX ? wrapColumn(rawCol) : rawCol;
        const index = row * width + col;

        if (isLand[index] === 0) {
          // Over water: evaporate toward saturation.
          humidity += (capacity - humidity) * params.evaporationRate;
        } else {
          const previousCol = wrapX ? wrapColumn(rawCol - wind) : Math.min(width - 1, Math.max(0, rawCol - wind));
          const climb = Math.max(0, elevation[index]! - elevation[row * width + previousCol]!);
          // Orographic lift: air forced upward sheds water, and everything downwind
          // of the range gets what is left. This is where rain shadows come from.
          const rainFraction = Math.min(0.95, params.rainfallRate + climb * params.orographicRate);
          humidity = Math.max(0, humidity - humidity * rainFraction);
        }

        const recording = !wrapX || step >= width;
        if (recording) {
          moisture[index] = isLand[index] === 0 ? 1 : Math.min(1, humidity / Math.max(1e-6, capacity));
        }
      }
    }
  }

  return { temperature, moisture };
}
